import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React from "react";
import { useNavigate } from "react-router-dom";
import { decodeClaimToken, getManagedCompanyId, isAdminClaimSession } from "@/app/services/companies/claimSession.js";
import { useCompany, useCompanyJobs } from "@/app/services/create-resume/queries.js";

import { BILLING_HISTORY, DEFAULT_BANNER_COLOR, PLAN_LABELS, SETUP_CHECKLIST_ITEMS, STORAGE_KEYS } from "./constants";
import { getCompanyDashboardPath, getCompanyPublicPath, getCompanyUpgradePath, getCompanyVerifiedPath } from "./paths";

dayjs.extend(relativeTime);

const CompanyDashboardContext = React.createContext(null);
const LEGACY_FALLBACK_VALUES = {
	companyName: "Your Company",
	industry: "Company",
	primaryColor: DEFAULT_BANNER_COLOR,
	selectedTemplate: "classic",
	plan: "starter",
};

function hasOwn(object, key) {
	return Object.prototype.hasOwnProperty.call(object, key);
}

function safeParse(value, fallback) {
	try {
		return value ? JSON.parse(value) : fallback;
	} catch {
		return fallback;
	}
}

function normalizeText(value) {
	return typeof value === "string" ? value.trim() : "";
}

function normalizeDetailText(item) {
	if (!item) return "";
	if (typeof item === "string") return normalizeText(item);
	if (typeof item === "object") return normalizeText(item.value);
	return "";
}

function hasMeaningfulSocialLinks(links) {
	return Array.isArray(links) && links.some((item) => normalizeText(item?.url));
}

function normalizeSocialLinks(links) {
	if (!Array.isArray(links)) return [];
	return links
		.map((item) => ({
			platform: normalizeText(item?.platform),
			url: normalizeText(item?.url),
		}))
		.filter((item) => item.platform || item.url);
}

function normalizeBenefits(benefits) {
	if (!Array.isArray(benefits)) return [];
	return benefits.map((item) => normalizeDetailText(item)).filter(Boolean);
}

function arraysEqual(a, b) {
	return JSON.stringify(a) === JSON.stringify(b);
}

function markFieldsEdited(current, fields) {
	const next = current && typeof current === "object" ? { ...current } : {};
	fields.forEach((field) => {
		next[field] = true;
	});
	return next;
}

function ensureUrl(value) {
	if (!value || typeof value !== "string") return "";
	return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

function normalizeEmploymentType(value) {
	switch (value) {
		case "Full-Time":
			return "Full-time";
		case "Part-Time":
			return "Part-time";
		case "Contractor":
			return "Contract";
		case "Temporary":
			return "Temporary";
		case "Internship":
			return "Internship";
		default:
			return value || "Other";
	}
}

function normalizeExperienceLevel(value, preferredYears) {
	if (value === "Senior-level(6+ yrs)" || preferredYears >= 6) return "Senior";
	if (value === "Mid-level(3-5 yrs)" || preferredYears >= 3) return "Mid-Level";
	return "Junior";
}

function detailsToText(items) {
	return (
		items
			?.map((item) => normalizeDetailText(item))
			.filter(Boolean)
			.join("\n") || ""
	);
}

function formatLocation(location, streetAddress) {
	const parts = [streetAddress, location?.city, location?.region, location?.country].filter(Boolean);
	return parts.join(", ") || "Location not specified";
}

function toDateString(value) {
	if (!value) return "";
	if (typeof value === "string") {
		const parsed = dayjs(value);
		return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
	}

	if (!value.year || !value.month || !value.day) return "";
	return dayjs(new Date(value.year, value.month - 1, value.day)).format("YYYY-MM-DD");
}

function toRelativeDate(value) {
	if (!value) return "Recently";
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed.fromNow() : "Recently";
}

function isClosedJob(jobStatus, deadline) {
	if (jobStatus && ["Closed", "Stopped", "Rejected"].includes(jobStatus)) {
		return true;
	}

	return !!deadline && dayjs(deadline).isBefore(dayjs(), "day");
}

function formatSalary(salary) {
	if (!salary || (!salary.minPay && !salary.maxPay)) return "";
	const formatter = new Intl.NumberFormat("en-US");
	const currency = salary.payCurrency || "TZS";
	const frequency = salary.payFrequency ? ` / ${salary.payFrequency.toLowerCase()}` : "";

	if (salary.payOption === "Range" && salary.minPay && salary.maxPay) {
		return `${currency} ${formatter.format(salary.minPay)} - ${formatter.format(salary.maxPay)}${frequency}`;
	}

	const exactAmount = salary.maxPay || salary.minPay;
	return exactAmount ? `${currency} ${formatter.format(exactAmount)}${frequency}` : "";
}

function normalizeCompanyJob(job) {
	const deadline = toDateString(job.deadlineDate) || toDateString(job.closeAt);
	const closed = isClosedJob(job.jobStatus, deadline);
	const company = typeof job.company === "string" ? job.company : job.company?.company_name || job.companyName;

	return {
		id: job._id,
		title: job.jobTitle || "Untitled role",
		company,
		location: formatLocation(job.jobLocation, job.jobStreetAddress),
		type: normalizeEmploymentType(job.employmentType),
		level: normalizeExperienceLevel(
			job.experienceQualification?.minimumLevelOfExperience,
			job.experienceQualification?.preferredYearsOfExperience,
		),
		status: closed ? "filled" : "open",
		postedDate: toRelativeDate(job.postedAt || job.createdAt),
		deadline,
		closedDate: closed ? toRelativeDate(deadline || job.closeAt) : "",
		views: job.viewsCount || 0,
		applicants: Math.max(0, Math.round((job.viewsCount || 0) * 0.08)),
		source: job.jobProcessingType === "SCREEN_ONLY" ? "crawler" : "manual",
		lastRefreshed: toRelativeDate(job.lastUpdatedAt),
		description: job.jobDescription?.lookingToHire || "",
		responsibilities: detailsToText(job.jobDescription?.responsibilities),
		qualifications: detailsToText(job.jobDescription?.qualifications),
		applicationProcess: job.applicationProcess || "",
		salary: formatSalary(job.salary),
		logoUrl: typeof job.company === "object" ? job.company?.logo_url : undefined,
		rawJobStatus: job.jobStatus,
		jobReferenceId: job.jobReferenceId,
		raw: job,
	};
}

function buildInitialBranding(company) {
	const socialLinks = [];

	[
		["LinkedIn", company?.linkedin_url],
		["Twitter", company?.twitter_url],
		["Facebook", company?.facebook_url],
		["Instagram", company?.instagram_url],
		["Website", company?.company_website ? ensureUrl(company.company_website) : ""],
	].forEach(([platform, url]) => {
		if (url) {
			socialLinks.push({ platform, url });
		}
	});

	return {
		companyName: company?.company_name || "Your Company",
		industry: company?.company_industry || "Company",
		about: company?.about_us || "",
		website: company?.company_website || "",
		email: company?.contact_email || "",
		phone: company?.phone_number || "",
		address: company?.headquarters || "",
		socialLinks,
		whyWorkWithUs: normalizeBenefits(company?.benefits).length
			? normalizeBenefits(company.benefits)
			: [
					"Meaningful work with visible impact",
					"Growth opportunities across teams",
					"Collaborative, candidate-first culture",
				],
		primaryColor: company?.banner_color || DEFAULT_BANNER_COLOR,
		selectedTemplate: "classic",
		companyLogo: company?.logo_url || null,
		plan: "starter",
	};
}

function inferLegacyEditedFields(savedState, defaults) {
	if (!savedState || typeof savedState !== "object") return {};

	const editedFields = {};
	const textFields = ["companyName", "industry", "about", "website", "email", "phone", "address"];

	textFields.forEach((field) => {
		const savedValue = normalizeText(savedState[field]);
		if (!savedValue) return;
		if (savedValue === LEGACY_FALLBACK_VALUES[field]) return;
		if (savedValue !== normalizeText(defaults[field])) {
			editedFields[field] = true;
		}
	});

	if (
		hasMeaningfulSocialLinks(savedState.socialLinks) &&
		!arraysEqual(normalizeSocialLinks(savedState.socialLinks), normalizeSocialLinks(defaults.socialLinks))
	) {
		editedFields.socialLinks = true;
	}

	if (
		normalizeBenefits(savedState.whyWorkWithUs).length &&
		!arraysEqual(normalizeBenefits(savedState.whyWorkWithUs), normalizeBenefits(defaults.whyWorkWithUs))
	) {
		editedFields.whyWorkWithUs = true;
	}

	["primaryColor", "selectedTemplate", "plan"].forEach((field) => {
		if (!hasOwn(savedState, field)) return;
		const savedValue = savedState[field];
		if (savedValue === undefined) return;
		if (savedValue === LEGACY_FALLBACK_VALUES[field] && savedValue === defaults[field]) return;
		if (savedValue !== defaults[field]) {
			editedFields[field] = true;
		}
	});

	return editedFields;
}

function readStoredDashboardState(companyId) {
	const savedState = safeParse(localStorage.getItem(STORAGE_KEYS.buildDashboardStateKey(companyId)), null);
	if (!savedState || typeof savedState !== "object") {
		return { draftBranding: null, editedFields: {} };
	}

	if (hasOwn(savedState, "branding") || hasOwn(savedState, "editedFields")) {
		return {
			draftBranding: savedState.branding && typeof savedState.branding === "object" ? savedState.branding : null,
			editedFields:
				savedState.editedFields && typeof savedState.editedFields === "object" ? savedState.editedFields : {},
		};
	}

	return {
		draftBranding: savedState,
		editedFields: null,
	};
}

function mergeDashboardState(draftBranding, editedFields, defaults) {
	if (!draftBranding || typeof draftBranding !== "object") {
		return defaults;
	}

	const nextState = { ...defaults };
	const overrideFields = [
		"companyName",
		"industry",
		"about",
		"website",
		"email",
		"phone",
		"address",
		"socialLinks",
		"whyWorkWithUs",
		"primaryColor",
		"selectedTemplate",
		"plan",
	];

	overrideFields.forEach((field) => {
		if (!editedFields?.[field] || !hasOwn(draftBranding, field)) return;

		if (field === "socialLinks") {
			nextState.socialLinks = Array.isArray(draftBranding.socialLinks) ? draftBranding.socialLinks : [];
			return;
		}

		if (field === "whyWorkWithUs") {
			nextState.whyWorkWithUs = Array.isArray(draftBranding.whyWorkWithUs) ? draftBranding.whyWorkWithUs : [];
			return;
		}

		nextState[field] = draftBranding[field];
	});

	return nextState;
}

function buildChecklist(branding) {
	const progress = {
		company_description: Boolean(branding.about?.trim()),
		contact_info: Boolean(
			branding.website?.trim() || branding.email?.trim() || branding.phone?.trim() || branding.address?.trim(),
		),
		social_links: Boolean(branding.socialLinks?.some((item) => item.url?.trim())),
		customize_banner: normalizeText(branding.primaryColor).toLowerCase() !== DEFAULT_BANNER_COLOR.toLowerCase(),
		why_work_here: Boolean(branding.whyWorkWithUs?.some((item) => item?.trim())),
	};

	const items = SETUP_CHECKLIST_ITEMS.map((item) => ({
		...item,
		completed: progress[item.id],
	}));
	const completedCount = items.filter((item) => item.completed).length;
	return {
		items,
		completedCount,
		totalCount: items.length,
		progressPercentage: items.length ? Math.round((completedCount / items.length) * 100) : 0,
		isSetupComplete: completedCount === items.length,
	};
}

function buildStats(jobs) {
	const openJobs = jobs.filter((job) => job.status === "open");
	const closedJobs = jobs.filter((job) => job.status === "filled");
	const totalViews = jobs.reduce((sum, job) => sum + (job.views || 0), 0);
	const totalCandidates = jobs.reduce((sum, job) => sum + (job.applicants || 0), 0);

	return {
		openJobs: openJobs.length,
		closedJobs: closedJobs.length,
		totalViews,
		totalCandidates,
	};
}

export function CompanyDashboardProvider({ children }) {
	const navigate = useNavigate();
	const claimSession = React.useMemo(() => decodeClaimToken(), []);
	const companyId = React.useMemo(() => getManagedCompanyId(), []);
	const isAdminAccess = React.useMemo(() => isAdminClaimSession(), []);
	const {
		data: companyResponse,
		isLoading: isCompanyLoading,
		isError: isCompanyError,
	} = useCompany(companyId, {
		authMode: "claim",
	});
	const jobsQuery = useCompanyJobs(companyId, {
		authMode: "claim",
	});

	const company = companyResponse?.data || null;
	const jobs = React.useMemo(() => {
		if (!Array.isArray(jobsQuery.data?.data)) return [];
		return jobsQuery.data.data.map(normalizeCompanyJob);
	}, [jobsQuery.data]);

	const apiBranding = React.useMemo(() => buildInitialBranding(company), [company]);
	const [draftBranding, setDraftBranding] = React.useState(null);
	const [editedFields, setEditedFields] = React.useState({});
	const [isDashboardStateHydrated, setIsDashboardStateHydrated] = React.useState(false);
	const [upsellDismissedAt, setUpsellDismissedAt] = React.useState(() => {
		const value = localStorage.getItem(STORAGE_KEYS.buildUpsellKey(companyId));
		return value || null;
	});

	React.useEffect(() => {
		const savedState = readStoredDashboardState(companyId);
		setDraftBranding(savedState.draftBranding);
		setEditedFields(savedState.editedFields);
		setUpsellDismissedAt(localStorage.getItem(STORAGE_KEYS.buildUpsellKey(companyId)));
		setIsDashboardStateHydrated(true);
	}, [companyId]);

	const resolvedEditedFields = React.useMemo(() => {
		if (editedFields === null) {
			return inferLegacyEditedFields(draftBranding, apiBranding);
		}
		return editedFields;
	}, [apiBranding, draftBranding, editedFields]);

	const branding = React.useMemo(
		() => mergeDashboardState(draftBranding, resolvedEditedFields, apiBranding),
		[apiBranding, draftBranding, resolvedEditedFields],
	);

	React.useEffect(() => {
		if (!isDashboardStateHydrated) return;

		const hasEditedValues = resolvedEditedFields && Object.keys(resolvedEditedFields).length > 0;

		if (!hasEditedValues) {
			localStorage.removeItem(STORAGE_KEYS.buildDashboardStateKey(companyId));
			return;
		}

		localStorage.setItem(
			STORAGE_KEYS.buildDashboardStateKey(companyId),
			JSON.stringify({
				branding: draftBranding,
				editedFields: resolvedEditedFields,
			}),
		);
	}, [companyId, draftBranding, isDashboardStateHydrated, resolvedEditedFields]);

	const profileData = safeParse(localStorage.getItem("profileData"), {});
	const companyName = branding.companyName || company?.company_name || "Company";
	const companySlug = React.useMemo(() => {
		const seed = company?.company_name || company?._id || companyId || "company";
		return seed
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/(^-|-$)/g, "");
	}, [company?._id, company?.company_name, companyId]);

	const checklist = React.useMemo(() => buildChecklist(branding), [branding]);
	const stats = React.useMemo(() => buildStats(jobs), [jobs]);

	const user = React.useMemo(() => {
		const fullName = [profileData.firstName, profileData.lastName].filter(Boolean).join(" ").trim();
		return {
			name: fullName || profileData.name || "Company manager",
			email: profileData.email || claimSession?.email || "manager@dproz.com",
			avatar: profileData.picture || "",
		};
	}, [
		claimSession?.email,
		profileData.email,
		profileData.firstName,
		profileData.lastName,
		profileData.name,
		profileData.picture,
	]);

	const updateBranding = React.useCallback((updates) => {
		const fields = Object.keys(updates);
		setDraftBranding((current) => ({ ...(current || {}), ...updates }));
		setEditedFields((current) => markFieldsEdited(current, fields));
	}, []);

	const updateSocialLinks = React.useCallback((nextLinks) => {
		setDraftBranding((current) => ({ ...(current || {}), socialLinks: nextLinks }));
		setEditedFields((current) => markFieldsEdited(current, ["socialLinks"]));
	}, []);

	const updateBenefits = React.useCallback((nextBenefits) => {
		setDraftBranding((current) => ({ ...(current || {}), whyWorkWithUs: nextBenefits }));
		setEditedFields((current) => markFieldsEdited(current, ["whyWorkWithUs"]));
	}, []);

	const clearBrandingDraft = React.useCallback(() => {
		setDraftBranding(null);
		setEditedFields({});
	}, []);

	const dismissUpsell = React.useCallback(() => {
		const now = new Date().toISOString();
		localStorage.setItem(STORAGE_KEYS.buildUpsellKey(companyId), now);
		setUpsellDismissedAt(now);
	}, [companyId]);

	const resetUpsell = React.useCallback(() => {
		localStorage.removeItem(STORAGE_KEYS.buildUpsellKey(companyId));
		setUpsellDismissedAt(null);
	}, [companyId]);

	const previewCompanyPage = React.useCallback(() => {
		const previewPath = isAdminAccess
			? getCompanyVerifiedPath(companyId, { preview: "dashboard" })
			: getCompanyPublicPath(companyId);
		window.open(previewPath, "_blank", "noopener,noreferrer");
	}, [companyId, isAdminAccess]);

	const shareCompanyPage = React.useCallback(async () => {
		const url = `${window.location.origin}${getCompanyPublicPath(companyId)}`;
		if (navigator.share) {
			try {
				await navigator.share({ title: `${companyName} on Dproz`, url });
				return;
			} catch {
				// fall back to clipboard
			}
		}
		await navigator.clipboard.writeText(url);
	}, [companyId, companyName]);

	const copyEmbedCode = React.useCallback(async () => {
		const iframe = `<iframe src="${window.location.origin}${getCompanyPublicPath(companyId)}" title="${companyName} jobs on Dproz" style="width:100%;min-height:720px;border:0;border-radius:16px;"></iframe>`;
		await navigator.clipboard.writeText(iframe);
	}, [companyId, companyName]);

	const goToTab = React.useCallback(
		(tab, hash) => {
			navigate(getCompanyDashboardPath(tab, hash));
		},
		[navigate],
	);

	const goToUpgrade = React.useCallback(() => {
		navigate(getCompanyUpgradePath());
	}, [navigate]);

	const value = React.useMemo(
		() => ({
			companyId,
			isAdminAccess,
			company,
			companyName,
			companySlug,
			user,
			branding,
			plan: branding.plan || "starter",
			planLabel: PLAN_LABELS[branding.plan || "starter"],
			jobs,
			stats,
			checklist,
			billingHistory: BILLING_HISTORY,
			isCompanyLoading,
			isCompanyError,
			jobsQuery,
			upsellDismissedAt,
			updateBranding,
			updateSocialLinks,
			updateBenefits,
			clearBrandingDraft,
			dismissUpsell,
			resetUpsell,
			goToTab,
			goToUpgrade,
			previewCompanyPage,
			shareCompanyPage,
			copyEmbedCode,
		}),
		[
			companyId,
			isAdminAccess,
			company,
			companyName,
			companySlug,
			user,
			branding,
			jobs,
			stats,
			checklist,
			isCompanyLoading,
			isCompanyError,
			jobsQuery,
			upsellDismissedAt,
			updateBranding,
			updateSocialLinks,
			updateBenefits,
			clearBrandingDraft,
			dismissUpsell,
			resetUpsell,
			goToTab,
			goToUpgrade,
			previewCompanyPage,
			shareCompanyPage,
			copyEmbedCode,
		],
	);

	return <CompanyDashboardContext.Provider value={value}>{children}</CompanyDashboardContext.Provider>;
}

export function useCompanyDashboard() {
	const context = React.useContext(CompanyDashboardContext);
	if (!context) {
		throw new Error("useCompanyDashboard must be used within CompanyDashboardProvider");
	}
	return context;
}
